import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import "./styles/main.css";
import App from "./App";
import { BrowserRouter } from "react-router-dom";
import { ToastContainer } from "react-toastify"; // Import ToastContainer
import "react-toastify/dist/ReactToastify.css"; // Import toast styles
import { Provider } from 'react-redux';
import { store } from "./store/store";
import { initSentry } from "./utils/sentry";
import { applyCSSVariables } from "./utils/cssVariables";
import SentryErrorBoundary from "./components/common/SentryErrorBoundary";
import { sendWebVitalsToSentry } from "./reportWebVitals";
import { getHostScopedRewritePath } from "./api/apiHelpers";
import { resolveCustomDomainBoot } from "./api/customDomainBoot";
import { isCustomDomainHost } from "./api/customDomainHost";
import UnknownHostPage from "./components/common/UnknownHostPage";

// Initialize Sentry before rendering the app
initSentry();

// Define the brand CSS variables BEFORE the first render, using the neutral
// fallbacks. applyCSSVariables is otherwise only called once tenant settings
// load, which leaves every pre-tenant screen (the tenant finder, and the first
// paint of any page) with --color-primary undefined.
//
// An undefined var is not merely "no colour" — it invalidates the whole
// declaration it appears in. Measured in the browser:
//
//   --tw-ring-color: var(--unset)  ->  box-shadow computes to "none"
//   --tw-ring-color: rgb(255 0 0)  ->  box-shadow renders the ring
//
// Every Input and Select used to carry focus:ring-[var(--color-primary)], so
// before this call those controls had NO VISIBLE FOCUS RING at all — a
// keyboard accessibility failure, not a cosmetic one. (Since 2026-10-31 focus
// rings use the platform --cc-focus from index.css; the club's color is for
// navigation selection only, but the vars are still set here for that.) Passing null makes the function
// take its own DEFAULT_PRIMARY/DEFAULT_SECONDARY path and derive every hover /
// soft / text / contrast-safe variable from them, so there is no second copy
// of that colour maths to drift.
//
// App.tsx calls this again with real settings once they load; that overrides
// these and is the only place tenant colours come from.
applyCSSVariables(null);

// Recover from stale lazy-chunk loads after a deploy. Netlify ships a new
// build with content-hashed chunk filenames; a user still running the
// previous index.html who navigates to a React.lazy route (we use lazy
// widely in App.tsx) requests a chunk that now 404s. The dynamic import
// rejects and — depending on where it's triggered — can leave the screen
// blank instead of hitting the error boundary. Detect that specific
// failure and reload ONCE to pull the fresh index.html + chunk hashes.
// sessionStorage-guarded so a genuinely-missing chunk can't loop-reload.
(function installChunkReloadGuard() {
  const isChunkLoadError = (err: any): boolean => {
    if (!err) return false;
    if (err.name === "ChunkLoadError") return true;
    const msg = String(err.message || err);
    return (
      /Loading chunk [\w-]+ failed/i.test(msg) ||
      /Loading CSS chunk [\w-]+ failed/i.test(msg) ||
      /error loading dynamically imported module/i.test(msg) ||
      /Importing a module script failed/i.test(msg)
    );
  };
  const RELOAD_KEY = "cc_chunk_reload_at";
  const reloadOnce = (err: any) => {
    if (!isChunkLoadError(err)) return;
    try {
      const last = Number(window.sessionStorage.getItem(RELOAD_KEY) || 0);
      // Skip if we already reloaded in the last 10s — the chunk is really
      // gone (bad build), so surface the error instead of looping.
      if (Date.now() - last < 10000) return;
      window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    } catch {
      // sessionStorage blocked (private mode / disabled) — reload anyway;
      // a one-shot reload is still better than a blank page.
    }
    window.location.reload();
  };
  window.addEventListener("error", (e) => reloadOnce((e as ErrorEvent).error || e));
  window.addEventListener("unhandledrejection", (e) =>
    reloadOnce((e as PromiseRejectionEvent).reason),
  );
})();

// Phase 1 custom domains (subdomains): on a tenant front-door host
// (`<slug>.clubcentral.app`) whose path doesn't already carry the tenant
// slug, inject it as the first path segment BEFORE React Router reads the
// URL — so every existing `/:clubid/...` route and `/${tenantId}/...` link
// works unchanged. `history.replaceState` (not a redirect) → no reload,
// no flash, no loop. On platform hosts this is a no-op (the helper returns
// null), so behavior is identical to today until a `*.clubcentral.app`
// subdomain is actually provisioned.
function injectHostTenantSlug() {
  try {
    const newPath = getHostScopedRewritePath();
    if (newPath) window.history.replaceState(null, "", newPath);
  } catch {
    // window/history unavailable (e.g. SSR) — no-op
  }
}

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

function renderApp() {
  injectHostTenantSlug();
  root.render(
    <React.StrictMode>
      <SentryErrorBoundary>
        <Provider store={store}>
          <BrowserRouter>
            {/* Place ToastContainer here so it's available globally */}
            <ToastContainer />
            <App />
          </BrowserRouter>
        </Provider>
      </SentryErrorBoundary>
    </React.StrictMode>
  );
}

// Phase 2 custom domains (PM 01fda321): on a club's own domain the boot first
// asks the backend which club this host is (active domains only), then
// renders the app with the slug injected exactly as on a subdomain. An
// unknown host, or a domain that is not active, gets the not-found page and
// never a default club. Every other host (the platform, staging, localhost,
// <slug>.clubcentral.app) renders synchronously, exactly as before.
let bootHostname = "";
try {
  bootHostname = window.location.hostname;
} catch {
  bootHostname = "";
}
if (isCustomDomainHost(bootHostname)) {
  resolveCustomDomainBoot({
    hostname: bootHostname,
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
  }).then(
    (boot) => {
      if (boot.status === "found") {
        renderApp();
      } else if (boot.status === "platform") {
        renderApp();
      } else {
        root.render(<UnknownHostPage host={boot.host} reason={boot.status} />);
      }
    },
    () => root.render(<UnknownHostPage host={bootHostname} reason="error" />),
  );
} else {
  renderApp();
}

// Send Web Vitals to Sentry for performance monitoring
// Learn more: https://bit.ly/CRA-vitals
sendWebVitalsToSentry();
